import { useCallback } from 'react';
import { useService } from '@atlassian/jira-common-services/src/use-service';
import { performPostRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import type { projectKeyType, DataState, RuleSetId } from '../../common/types';
import type {
	ContentPreviewContent,
	ContentPreviewInitialData,
} from '../../common/types/content-preview';

type ContentPreviewResponse = {
	renderedPreview: string;
};

export const useEmailTemplatePanelContentPreview = (
	content: ContentPreviewContent,
	initialData: ContentPreviewInitialData,
	projectKey: projectKeyType,
	ruleSetId?: RuleSetId,
): DataState<ContentPreviewResponse> & {
	fetch: () => Promise<ContentPreviewResponse | undefined>;
} => {
	const contentPreviewRequest = useCallback(() => {
		if (ruleSetId) {
			return performPostRequest('/rest/servicedesk/notifications/1/content-preview/ruleset', {
				body: JSON.stringify({
					languageTag: initialData.languageTag,
					projectKey,
					ruleSetId,
					format: initialData.format,
					contentTemplate: content.textTemplate,
				}),
			});
		}

		return performPostRequest('/rest/servicedesk/notifications/1/content-preview', {
			body: JSON.stringify({
				languageTag: initialData.languageTag,
				projectKey,
				contexts: initialData.contexts,
				format: initialData.format,
				stylingData: {
					subjectTemplate: content.subjectTemplate,
					htmlTemplate: content.htmlTemplate,
					textTemplate: content.textTemplate,
					css: content.css,
				},
			}),
		});
	}, [
		ruleSetId,
		initialData.languageTag,
		initialData.format,
		initialData.contexts,
		projectKey,
		content.textTemplate,
		content.subjectTemplate,
		content.htmlTemplate,
		content.css,
	]);

	const { data, loading, error, fetch } = useService<ContentPreviewResponse>(contentPreviewRequest);
	return {
		data,
		loading,
		error,
		fetch,
	};
};
